import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import './ProtocoloDetalhes.css';

import { API_URL } from '../config';
import { situacaoExameLabel } from '../utils/situacaoExame';
import { formatDate, formatDateTime, formatTime, formatDateOfBirth } from '../utils/formatDate';

function ProtocoloDetalhes() {
  const { protocolo } = useParams();
  const navigate = useNavigate();
  const [solicitacao, setSolicitacao] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [abaAtiva, setAbaAtiva] = useState('paciente');
  const [anexos, setAnexos] = useState([]);
  const [loadingAnexos, setLoadingAnexos] = useState(false);

  useEffect(() => {
    fetchProtocoloDetalhes();
  }, [protocolo]);

  useEffect(() => {
    if (solicitacao?.ID) {
      fetchAnexos();
    }
  }, [solicitacao?.ID]);

  const fetchProtocoloDetalhes = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await axios.get(`${API_URL}/solicitacoes/protocolo/${protocolo}`);
      setSolicitacao(response.data);
    } catch (err) {
      setError('Erro ao carregar dados da solicitação: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  // formatDate, formatDateTime, formatTime importados de ../utils/formatDate

  const formatCurrency = (value) => {
    if (!value) return 'R$ 0,00';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const calcularIdade = (dataNascimento) => {
    if (!dataNascimento) return '-';
    const hoje = new Date();
    const nascimento = new Date(dataNascimento);
    let idade = hoje.getFullYear() - nascimento.getFullYear();
    const mes = hoje.getMonth() - nascimento.getMonth();
    if (mes < 0 || (mes === 0 && hoje.getDate() < nascimento.getDate())) {
      idade--;
    }
    return `${idade} anos`;
  };

  const formatarValor = (value) => {
    if (value === null || value === undefined || value === '') {
      return '-';
    } else if (typeof value === 'boolean') {
      return value ? 'Sim' : 'Não';
    } else if (typeof value === 'string') {
      // Converter T/F para Sim/Não
      if (value === 'T' || value === 't') return 'Sim';
      if (value === 'F' || value === 'f') return 'Não';
      return value;
    } else if (typeof value === 'number') {
      return value.toString();
    } else {
      return String(value);
    }
  };

  const fetchAnexos = async () => {
    try {
      setLoadingAnexos(true);
      const response = await axios.get(`${API_URL}/solicitacoes/${solicitacao.ID}/anexos`);
      setAnexos(response.data);
    } catch (err) {
      console.error('Erro ao carregar anexos:', err);
    } finally {
      setLoadingAnexos(false);
    }
  };

  const abrirAnexo = async (anexoId, nomeArquivo) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/anexos/${anexoId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Erro ao carregar anexo');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 100);
    } catch (error) {
      console.error('Erro ao abrir anexo:', error);
      alert('Erro ao carregar anexo');
    }
  };

  const formatarTamanhoArquivo = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  };

  if (loading) {
    return <div className="loading">Carregando detalhes da solicitação...</div>;
  }

  if (error) {
    return (
      <div className="error-container">
        <p className="error">{error}</p>
        <button onClick={() => navigate(-1)} className="back-button">
          Voltar
        </button>
      </div>
    );
  }

  if (!solicitacao) {
    return <div className="loading">Solicitação não encontrada</div>;
  }

  // Definir abas disponíveis
  const abas = [
    { id: 'paciente', label: '👤 Paciente', show: true },
    { id: 'solicitacao', label: '📋 Solicitação', show: true },
    { id: 'valores', label: '💰 Valores', show: true },
    { id: 'exames', label: '🔬 Exames', show: solicitacao.EXAMES },
    { id: 'clinico', label: '🏥 Dados Clínicos', show: true },
    { id: 'observacoes', label: '📄 Observações', show: solicitacao.OBSERVACAO1 || solicitacao.OBSERVACAO2 || solicitacao.OBSERVACAO3 || solicitacao.OBS_RECIBO || solicitacao.OBS_FATURAMENTO },
    { id: 'resultados', label: '📊 Resultados', show: solicitacao.exames && solicitacao.exames.length > 0 },
    { id: 'anexos', label: '📎 Anexos', show: anexos.length > 0 },
    { id: 'adicionais', label: 'ℹ️ Informações Adicionais', show: true }
  ].filter(aba => aba.show);

  return (
    <div className="protocolo-detalhes-page">
      <div className="page-header-detalhes">
        <button onClick={() => navigate(-1)} className="back-button">
          ← Voltar
        </button>
        <h1>Protocolo: {solicitacao.PROTOCOLO}</h1>
        <button
          onClick={() => navigate(`/atendimentos/${solicitacao.ID}`)}
          className="btn-detail-primary"
        >
          Ver Atendimento Completo
        </button>
      </div>

      {/* Sistema de Abas */}
      <div className="detail-tabs">
        {abas.map(aba => (
          <button
            key={aba.id}
            onClick={() => setAbaAtiva(aba.id)}
            className={`detail-tab ${abaAtiva === aba.id ? 'active' : ''}`}
          >
            {aba.label}
          </button>
        ))}
      </div>

      {/* Aba: Informações do Paciente */}
      {abaAtiva === 'paciente' && (
      <div className="detalhes-grid">
        {/* Informações do Paciente */}
        <div className="info-card">
          <h2>👤 Informações do Paciente</h2>
          <div className="info-grid">
            <div className="info-item">
              <label>Nome:</label>
              <span>{solicitacao.PACIENTE_NOME || '-'}</span>
            </div>
            <div className="info-item">
              <label>CPF:</label>
              <span>{solicitacao.PACIENTE_CPF || '-'}</span>
            </div>
            <div className="info-item">
              <label>Data de Nascimento:</label>
              <span>{formatDate(solicitacao.PACIENTE_DATANASCIMENTO)}</span>
            </div>
            <div className="info-item">
              <label>Idade:</label>
              <span>{calcularIdade(solicitacao.PACIENTE_DATANASCIMENTO)}</span>
            </div>
            <div className="info-item">
              <label>Sexo:</label>
              <span>{solicitacao.PACIENTE_SEXO === 'M' ? 'Masculino' : solicitacao.PACIENTE_SEXO === 'F' ? 'Feminino' : formatarValor(solicitacao.PACIENTE_SEXO)}</span>
            </div>
            <div className="info-item">
              <label>Telefone:</label>
              <span>{solicitacao.PACIENTE_TELEFONE || '-'}</span>
            </div>
            <div className="info-item">
              <label>Email:</label>
              <span>{solicitacao.PACIENTE_EMAIL || '-'}</span>
            </div>
            <div className="info-item full-width">
              <label>Endereço:</label>
              <span>{solicitacao.PACIENTE_ENDERECO || '-'}</span>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Aba: Dados da Solicitação */}
      {abaAtiva === 'solicitacao' && (
      <div className="detalhes-grid">
        {/* Dados da Solicitação */}
        <div className="info-card">
          <h2>📋 Dados da Solicitação</h2>
          <div className="info-grid">
            <div className="info-item">
              <label>ID:</label>
              <span>{solicitacao.ID}</span>
            </div>
            <div className="info-item">
              <label>Local:</label>
              <span>{formatarValor(solicitacao.LOCAL)}</span>
            </div>
            <div className="info-item">
              <label>Protocolo:</label>
              <span><strong>{solicitacao.PROTOCOLO}</strong></span>
            </div>
            <div className="info-item">
              <label>Data:</label>
              <span>{formatDate(solicitacao.DATA)}</span>
            </div>
            <div className="info-item">
              <label>Horário:</label>
              <span>{formatTime(solicitacao.HORARIO)}</span>
            </div>
            <div className="info-item">
              <label>Data de Entrega:</label>
              <span>{formatDate(solicitacao.DATAENTREGA)}</span>
            </div>
            <div className="info-item">
              <label>Horário de Entrega:</label>
              <span>{formatTime(solicitacao.HORARIOENTREGA)}</span>
            </div>
            <div className="info-item">
              <label>Usuário:</label>
              <span>{formatarValor(solicitacao.USUARIO)}</span>
            </div>
            <div className="info-item">
              <label>Usuário Inclusão:</label>
              <span>{formatarValor(solicitacao.USUARIO_INCLUSAO)}</span>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Aba: Valores */}
      {abaAtiva === 'valores' && (
      <div className="detalhes-grid">
        {/* Valores */}
        <div className="info-card">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            💰 Valores
            {solicitacao.statusPagamento && (() => {
              const map = {
                PAGO: { label: 'Pago', bg: '#10b981' },
                NAO_PAGO: { label: 'Nao Pago', bg: '#ef4444' },
                PARCIAL: { label: 'Parcial', bg: '#f59e0b' },
                EXCEDENTE: { label: 'Excedente', bg: '#8b5cf6' },
                ISENTO: { label: 'Isento', bg: '#6b7280' }
              };
              const s = map[solicitacao.statusPagamento] || map.NAO_PAGO;
              return <span style={{ background: s.bg, color: '#fff', padding: '3px 10px', borderRadius: '10px', fontSize: '0.75rem', fontWeight: 'bold' }}>{s.label}</span>;
            })()}
          </h2>
          <div className="info-grid">
            <div className="info-item">
              <label>Subtotal:</label>
              <span className="valor">{formatCurrency(solicitacao.SUBTOTAL)}</span>
            </div>
            <div className="info-item">
              <label>Desconto:</label>
              <span className="valor">{formatCurrency(solicitacao.DESCONTO)}</span>
            </div>
            <div className="info-item">
              <label>Taxa Extra:</label>
              <span className="valor">{formatCurrency(solicitacao.TAXAEXTRA)}</span>
            </div>
            <div className="info-item">
              <label>Total Cobrado:</label>
              <span className="valor total">{formatCurrency(solicitacao.TOTAL)}</span>
            </div>
            <div className="info-item">
              <label>Total Pago:</label>
              <span className="valor" style={{ color: '#10b981', fontWeight: 700 }}>{formatCurrency(solicitacao.valorTotalPago)}</span>
            </div>
          </div>
        </div>

        {/* Pagamentos */}
        <div className="info-card">
          <h2>💳 Pagamentos ({solicitacao.pagamentos?.length || 0})</h2>
          {solicitacao.pagamentos && solicitacao.pagamentos.length > 0 ? (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: '8px', textAlign: 'left' }}>Forma</th>
                  <th style={{ padding: '8px', textAlign: 'left' }}>Data</th>
                  <th style={{ padding: '8px', textAlign: 'left' }}>Hora</th>
                  <th style={{ padding: '8px', textAlign: 'right' }}>Valor</th>
                  <th style={{ padding: '8px', textAlign: 'right' }}>Arred.</th>
                  <th style={{ padding: '8px', textAlign: 'left' }}>Atendente</th>
                </tr>
              </thead>
              <tbody>
                {solicitacao.pagamentos.map((pag, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid #f0f0f0' }}>
                    <td style={{ padding: '8px' }}>{pag.FORMA_PAGAMENTO || '-'}</td>
                    <td style={{ padding: '8px' }}>{formatDate(pag.DATA)}</td>
                    <td style={{ padding: '8px' }}>{formatTime(pag.HORA)}</td>
                    <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600 }}>{formatCurrency(pag.VALOR)}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>{pag.ARREDONDAMENTO != null && pag.ARREDONDAMENTO !== 0 ? formatCurrency(pag.ARREDONDAMENTO) : '-'}</td>
                    <td style={{ padding: '8px' }}>{pag.USUARIO || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ textAlign: 'center', padding: '1.5rem', color: '#6b7280' }}>
              Nenhum pagamento registrado
              {solicitacao.TOTAL > 0 && (
                <div style={{ color: '#ef4444', fontWeight: 'bold', marginTop: '0.5rem' }}>
                  Valor pendente: {formatCurrency(solicitacao.TOTAL)}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
      )}

      {/* Aba: Exames */}
      {abaAtiva === 'exames' && solicitacao.EXAMES && (
      <div className="detalhes-grid">
        {/* Exames */}
        {solicitacao.EXAMES && (
          <div className="info-card">
            <h2>🔬 Exames Solicitados</h2>
            <div className="exames-list">
              {solicitacao.EXAMES.split(/\s{2,}/).filter(e => e.trim()).map((exame, index) => (
                <div key={index} className="exame-item">
                  • {exame.trim()}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Siglas */}
        {(solicitacao.SIGLAS || solicitacao.SIGLASEXAME) && (
          <div className="info-card">
            <h2>📝 Siglas</h2>
            <div className="info-grid">
              {solicitacao.SIGLAS && (
                <div className="info-item full-width">
                  <label>Siglas:</label>
                  <span>{solicitacao.SIGLAS}</span>
                </div>
              )}
              {solicitacao.SIGLASEXAME && (
                <div className="info-item full-width">
                  <label>Siglas Exame:</label>
                  <span>{solicitacao.SIGLASEXAME}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      )}

      {/* Aba: Observações */}
      {abaAtiva === 'observacoes' && (
      <div className="detalhes-grid">
        {/* Observações */}
        {(solicitacao.OBSERVACAO1 || solicitacao.OBSERVACAO2 || solicitacao.OBSERVACAO3 || solicitacao.OBS_RECIBO || solicitacao.OBS_FATURAMENTO) && (
          <div className="info-card">
            <h2>📄 Observações</h2>
            <div className="info-grid">
              {solicitacao.OBSERVACAO1 && (
                <div className="info-item full-width">
                  <label>Observação 1:</label>
                  <span>{solicitacao.OBSERVACAO1}</span>
                </div>
              )}
              {solicitacao.OBSERVACAO2 && (
                <div className="info-item full-width">
                  <label>Observação 2:</label>
                  <span>{solicitacao.OBSERVACAO2}</span>
                </div>
              )}
              {solicitacao.OBSERVACAO3 && (
                <div className="info-item full-width">
                  <label>Observação 3:</label>
                  <span>{solicitacao.OBSERVACAO3}</span>
                </div>
              )}
              {solicitacao.OBS_RECIBO && (
                <div className="info-item full-width">
                  <label>Obs. Recibo:</label>
                  <span>{solicitacao.OBS_RECIBO}</span>
                </div>
              )}
              {solicitacao.OBS_FATURAMENTO && (
                <div className="info-item full-width">
                  <label>Obs. Faturamento:</label>
                  <span>{solicitacao.OBS_FATURAMENTO}</span>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      )}

      {/* Aba: Dados Clínicos */}
      {abaAtiva === 'clinico' && (
      <div className="detalhes-grid">
        {/* Dados Clínicos */}
        <div className="info-card">
          <h2>🏥 Dados Clínicos</h2>
          <div className="info-grid">
            <div className="info-item">
              <label>Peso:</label>
              <span>{solicitacao.PESO ? `${solicitacao.PESO} kg` : '-'}</span>
            </div>
            <div className="info-item">
              <label>Altura:</label>
              <span>{solicitacao.ALTURA ? `${solicitacao.ALTURA} m` : '-'}</span>
            </div>
            <div className="info-item">
              <label>Data Última Menstruação:</label>
              <span>{formatDate(solicitacao.DATAULTMENS)}</span>
            </div>
            <div className="info-item">
              <label>Gestação:</label>
              <span>{formatarValor(solicitacao.GESTACAO)}</span>
            </div>
            <div className="info-item">
              <label>CID:</label>
              <span>{formatarValor(solicitacao.CID)}</span>
            </div>
            <div className="info-item">
              <label>Leito:</label>
              <span>{formatarValor(solicitacao.LEITO)}</span>
            </div>
            <div className="info-item">
              <label>Jejum:</label>
              <span>{formatTime(solicitacao.JEJUM)}</span>
            </div>
            <div className="info-item">
              <label>Última Refeição:</label>
              <span>{formatTime(solicitacao.ULTIMA_REFEICAO)}</span>
            </div>
            {solicitacao.MEDICAMENTOS && (
              <div className="info-item full-width">
                <label>Medicamentos:</label>
                <span>{solicitacao.MEDICAMENTOS}</span>
              </div>
            )}
            {solicitacao.INDIC_CLINICA && (
              <div className="info-item full-width">
                <label>Indicação Clínica:</label>
                <span>{solicitacao.INDIC_CLINICA}</span>
              </div>
            )}
          </div>
        </div>
      </div>
      )}

      {/* Aba: Informações Adicionais */}
      {abaAtiva === 'adicionais' && (
      <div className="detalhes-grid">
        {/* Informações Adicionais */}
        <div className="info-card">
          <h2>ℹ️ Informações Adicionais</h2>
          <div className="info-grid">
            <div className="info-item">
              <label>Número Etiqueta:</label>
              <span>{formatarValor(solicitacao.NUMEROETIQUETA)}</span>
            </div>
            <div className="info-item">
              <label>Senha S-Line:</label>
              <span>{formatarValor(solicitacao.SENHA_SLINE)}</span>
            </div>
            <div className="info-item">
              <label>Caráter Solicitação:</label>
              <span>{formatarValor(solicitacao.CARATER_SOLICITACAO)}</span>
            </div>
            <div className="info-item">
              <label>Tipo Atendimento:</label>
              <span>{formatarValor(solicitacao.TIPO_ATENDIMENTO)}</span>
            </div>
            <div className="info-item">
              <label>Forma de Entrega:</label>
              <span>{formatarValor(solicitacao.FORMA_ENTREGA)}</span>
            </div>
            <div className="info-item">
              <label>Atendimento Domiciliar:</label>
              <span>{formatarValor(solicitacao.ATENDIMENTO_DOMICILIAR)}</span>
            </div>
            <div className="info-item">
              <label>Imprime Requisição:</label>
              <span>{formatarValor(solicitacao.IMPRIME_REQ)}</span>
            </div>
            <div className="info-item">
              <label>Envia S-Line Paciente:</label>
              <span>{formatarValor(solicitacao.ENVIA_SLINE_PAC)}</span>
            </div>
            <div className="info-item">
              <label>Enviar SMS:</label>
              <span>{formatarValor(solicitacao.ENVIAR_SMS)}</span>
            </div>
            <div className="info-item">
              <label>Enviar Email:</label>
              <span>{formatarValor(solicitacao.ENVIAR_NOTIFICACAO_EMAIL)}</span>
            </div>
            <div className="info-item">
              <label>Resultado Liberado:</label>
              <span>{formatarValor(solicitacao.RESULTADO_LIBERADO)}</span>
            </div>
            <div className="info-item">
              <label>Origem:</label>
              <span>{formatarValor(solicitacao.ORIGEM)}</span>
            </div>
          </div>
        </div>
      </div>
      )}

      {/* Aba: Exames e Resultados */}
      {abaAtiva === 'resultados' && solicitacao.exames && solicitacao.exames.length > 0 && (
        <div className="exames-resultados-section">
          <h2>🔬 Exames e Resultados ({solicitacao.exames.length})</h2>
          {solicitacao.exames.map((exame, index) => (
            <div key={exame.ID} className="exame-card">
              <div className="exame-header">
                <h3>
                  {index + 1}. {exame.EXAME} - {exame.EXAME_DESCRICAO || 'Sem descrição'}
                </h3>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  {exame.TEM_PDF === 1 && exame.PDF_EXAME_ID && (
                    <button
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem('token');
                          const response = await fetch(`${API_URL}/solicitacao-exames/${exame.PDF_EXAME_ID}/pdf`, {
                            headers: {
                              'Authorization': `Bearer ${token}`
                            }
                          });

                          if (!response.ok) {
                            throw new Error('Erro ao carregar PDF');
                          }

                          const blob = await response.blob();
                          const url = window.URL.createObjectURL(blob);
                          window.open(url, '_blank');

                          // Limpar a URL após um tempo
                          setTimeout(() => window.URL.revokeObjectURL(url), 100);
                        } catch (error) {
                          console.error('Erro ao abrir PDF:', error);
                          alert('Erro ao carregar PDF');
                        }
                      }}
                      className="btn-detail-danger"
                      title="Ver resultado em PDF"
                    >
                      Ver PDF
                    </button>
                  )}
                  <span style={{
                    padding: '3px 10px', borderRadius: '12px', fontSize: '0.75rem', fontWeight: '600',
                    backgroundColor: (exame.MATERIALCOLHIDO || '').trim() === 'T' ? '#e8f5e9' : '#fce4ec',
                    color: (exame.MATERIALCOLHIDO || '').trim() === 'T' ? '#2e7d32' : '#c62828'
                  }}>
                    {(exame.MATERIALCOLHIDO || '').trim() === 'T' ? 'Colhido' : 'Nao colhido'}
                  </span>
                  <span className={`situacao-badge situacao-${exame.SITUACAO}`}>
                    {situacaoExameLabel(exame.SITUACAO)}
                  </span>
                </div>
              </div>

              <div className="exame-info-grid">
                <div className="exame-info-item">
                  <label>Data Coleta:</label>
                  <span>{formatDate(exame.DATACOLETA)}</span>
                </div>
                <div className="exame-info-item">
                  <label>Hora Coleta:</label>
                  <span>{formatTime(exame.HORACOLETA)}</span>
                </div>
                <div className="exame-info-item">
                  <label>Material:</label>
                  <span>{exame.INFO_C_DESCMAT || '-'}</span>
                </div>
                <div className="exame-info-item">
                  <label>Material Colhido:</label>
                  <span>{formatarValor(exame.MATERIALCOLHIDO)}</span>
                </div>
                {exame.ASSINATURA && (
                  <div className="exame-info-item">
                    <label>Responsável Técnico:</label>
                    <span>
                      {exame.RESPONSAVEL_NOME
                        ? `${exame.RESPONSAVEL_NOME} (CRF: ${exame.ASSINATURA})`
                        : `CRF: ${exame.ASSINATURA}`
                      }
                    </span>
                  </div>
                )}
                {exame.USUARIO_DIGITACAO && (
                  <div className="exame-info-item">
                    <label>Usuário Digitação:</label>
                    <span>{exame.USUARIO_DIGITACAO}</span>
                  </div>
                )}
                {exame.DATA_HORA_DIG && (
                  <div className="exame-info-item">
                    <label>Data/Hora Digitação:</label>
                    <span>{formatDateTime(exame.DATA_HORA_DIG)}</span>
                  </div>
                )}
                {exame.USUARIO_COLETA && (
                  <div className="exame-info-item">
                    <label>Usuário Coleta:</label>
                    <span>{exame.USUARIO_COLETA}</span>
                  </div>
                )}
                {exame.USUARIO_CONFERENCIA && (
                  <div className="exame-info-item">
                    <label>Usuário Conferência:</label>
                    <span>{exame.USUARIO_CONFERENCIA}</span>
                  </div>
                )}
              </div>

              {exame.OBSERVACAO && (
                <div className="exame-observacao">
                  <label>Observação:</label>
                  <p>{exame.OBSERVACAO}</p>
                </div>
              )}

              {/* Resultados do Exame */}
              {exame.resultados && exame.resultados.length > 0 && (
                <div className="resultados-section">
                  <h4>📊 Resultados ({exame.resultados.filter(r => {
                    // Filtrar resultados vazios, marcadores de seção ou [TEXTO]/[IMAGEM]
                    const componente = r.COMPONENTE_DESCRICAO || r.COMPONENTE_LAUDO || '';
                    const resultado = r.RESULTADOMEMO || r.RESULTADO || '';

                    // Não mostrar se for apenas o código do exame repetido
                    if (componente === exame.EXAME || resultado === exame.EXAME) return false;

                    // Não mostrar marcadores especiais
                    if (resultado === '[TEXTO]' || resultado === '[IMAGEM]') return false;

                    return true;
                  }).length})</h4>
                  <div className="resultados-table-wrapper">
                    <table className="resultados-table">
                      <thead>
                        <tr>
                          <th>Componente</th>
                          <th>Resultado</th>
                          <th>Unidade</th>
                        </tr>
                      </thead>
                      <tbody>
                        {exame.resultados
                          .filter(r => {
                            // Filtrar resultados vazios, marcadores de seção ou [TEXTO]/[IMAGEM]
                            const componente = r.COMPONENTE_DESCRICAO || r.COMPONENTE_LAUDO || '';
                            const resultado = r.RESULTADOMEMO || r.RESULTADO || '';

                            // Não mostrar se for apenas o código do exame repetido
                            if (componente === exame.EXAME || resultado === exame.EXAME) return false;

                            // Não mostrar marcadores especiais
                            if (resultado === '[TEXTO]' || resultado === '[IMAGEM]') return false;

                            return true;
                          })
                          .map((resultado, idx) => {
                            // Limpar o resultado removendo " (0)" no final
                            let resultadoLimpo = (resultado.RESULTADOMEMO || resultado.RESULTADO || '-').trim();
                            resultadoLimpo = resultadoLimpo.replace(/\s*\(0\)\s*$/, '');

                            return (
                              <tr key={idx}>
                                <td>
                                  <strong>{resultado.COMPONENTE_DESCRICAO || resultado.COMPONENTE_LAUDO || `Código ${resultado.COMPONENTE}`}</strong>
                                </td>
                                <td>
                                  {resultadoLimpo}
                                  {resultado.RESULTADO2 && resultado.RESULTADO2 !== '0' && ` (${resultado.RESULTADO2})`}
                                </td>
                                <td>{resultado.COMPONENTE_UNIDADE || '-'}</td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {(!exame.resultados || exame.resultados.length === 0) && (
                <div className="no-resultados">
                  <p>Nenhum resultado disponível para este exame.</p>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Aba: Anexos */}
      {abaAtiva === 'anexos' && (
        <div className="anexos-section">
          <h2>📎 Anexos ({anexos.length})</h2>

          {loadingAnexos ? (
            <div className="loading">Carregando anexos...</div>
          ) : anexos.length === 0 ? (
            <div className="no-anexos">
              <p>Nenhum anexo disponível para este protocolo.</p>
            </div>
          ) : (
            <div className="anexos-list">
              {anexos.map((anexo) => (
                <div key={anexo.ID} className="anexo-card">
                  <div className="anexo-header">
                    <div className="anexo-icon">
                      {anexo.NOME_ARQUIVO?.toLowerCase().endsWith('.pdf') ? '📄' :
                       anexo.NOME_ARQUIVO?.toLowerCase().match(/\.(jpg|jpeg|png)$/) ? '🖼️' :
                       anexo.NOME_ARQUIVO?.toLowerCase().match(/\.(doc|docx)$/) ? '📝' : '📎'}
                    </div>
                    <div className="anexo-info">
                      <h3>{anexo.DESCRICAO || 'Anexo sem descrição'}</h3>
                      <p className="anexo-filename">{anexo.NOME_ARQUIVO || `anexo_${anexo.ID}`}</p>
                      <p className="anexo-size">{formatarTamanhoArquivo(anexo.TAMANHO)}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => abrirAnexo(anexo.ID, anexo.NOME_ARQUIVO)}
                    className="btn-detail-primary"
                  >
                    Ver Anexo
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ProtocoloDetalhes;
